(() => {
  const registry = new WeakMap();

  class MiniGearProductGallery {
    constructor(root) {
      this.root = root;
      this.section = root.closest('product-info') || document;
      this.viewport = root.querySelector('[data-mg-gallery-viewport]');
      this.track = root.querySelector('[data-mg-gallery-track]');
      this.slides = Array.from(root.querySelectorAll('[data-mg-gallery-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-mg-gallery-dot]'));
      this.prev = root.querySelector('[data-mg-gallery-prev]');
      this.next = root.querySelector('[data-mg-gallery-next]');
      this.modal =
        root.nextElementSibling &&
        root.nextElementSibling.matches('[data-mg-gallery-modal]')
          ? root.nextElementSibling
          : document.querySelector('[data-mg-gallery-modal]');
      this.modalStage = this.modal
        ? this.modal.querySelector('[data-mg-gallery-modal-stage]')
        : null;
      this.modalCounter = this.modal
        ? this.modal.querySelector('[data-mg-gallery-modal-counter]')
        : null;
      this.modalPrev = this.modal
        ? this.modal.querySelector('[data-mg-gallery-modal-prev]')
        : null;
      this.modalNext = this.modal
        ? this.modal.querySelector('[data-mg-gallery-modal-next]')
        : null;
      this.closeButton = this.modal
        ? this.modal.querySelector('[data-mg-gallery-close]')
        : null;
      this.mobileMedia = window.matchMedia(
        '(max-width: 749px), (pointer: coarse) and (orientation: landscape) and (max-width: 989px) and (max-height: 520px)'
      );

      this.data = this.readData();
      this.modalMedia = this.buildModalMedia();
      this.slidePreloads = new Map();
      this.modalVideoTimes = new Map();
      this.index = 0;
      this.scrollY = 0;
      this.modalBuilt = false;
      this.modalIndex = 0;
      this.modalAnimating = false;
      this.modalTransitionTimer = null;
      this.modalRenderToken = 0;
      this.modalPreloaded = new Map();
      this.mobileWrapTimer = null;
      this.lastTouchMoveAt = 0;
      this.historyPushed = false;
      this.closingFromHistory = false;
      this.historyFallbackTimer = null;
      this.modalTouchStartX = 0;
      this.modalTouchStartY = 0;
      this.modalTrigger = null;

      this.drag = {
        active: false,
        locked: null,
        startX: 0,
        startY: 0,
        deltaX: 0,
      };

      this.bound = [];
      this.init();
    }

    readData() {
      const script = this.root.querySelector('[data-mg-gallery-json]');

      if (!script) {
        return {
          variantMedia: {},
        };
      }

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return {
          variantMedia: {},
        };
      }
    }

    isMobile() {
      return this.mobileMedia.matches;
    }

    on(target, event, handler, options) {
      if (!target) return;

      target.addEventListener(event, handler, options);
      this.bound.push([
        target,
        event,
        handler,
        options,
      ]);
    }

    isBlockingInteractiveMediaClick(event) {
      return Boolean(
        event &&
          event.target &&
          event.target.closest(
            'iframe, model-viewer, [data-shopify-xr]'
          )
      );
    }

    safePlayVideo(video) {
      if (!video || typeof video.play !== 'function') return;

      video.controls = false;
      video.removeAttribute('controls');
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }

    prepareVideo(video) {
      if (!video) return;
      video.controls = false;
      video.removeAttribute('controls');
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'none';
    }

    pauseAllSlideVideos() {
      this.slides.forEach((slide) => {
        slide.querySelectorAll('video').forEach((video) => {
          this.prepareVideo(video);
          video.pause();
        });
      });
    }

    syncSlideVideoPlayback() {
      if (this.modal && !this.modal.hidden) {
        this.pauseAllSlideVideos();
        return;
      }

      this.slides.forEach((slide, slideIndex) => {
        slide.querySelectorAll('video').forEach((video) => {
          this.prepareVideo(video);
          if (slideIndex === this.index) {
            this.safePlayVideo(video);
          } else {
            video.pause();
          }
        });
      });
    }

    preloadSlideImage(index) {
      if (!this.slides.length) return;
      const normalized = this.normalize(index);
      if (this.slidePreloads.has(normalized)) return;

      const slide = this.slides[normalized];
      const img = slide ? slide.querySelector('img') : null;
      if (!img) return;

      const preload = new Image();
      if (img.srcset) preload.srcset = img.srcset;
      if (img.sizes) preload.sizes = img.sizes;
      preload.decoding = 'async';
      if ('fetchPriority' in preload) preload.fetchPriority = 'low';
      preload.src = img.currentSrc || img.src;
      this.slidePreloads.set(normalized, preload);
    }

    preloadAdjacentSlides() {
      if (this.slides.length < 2) return;
      this.preloadSlideImage(this.index + 1);
      this.preloadSlideImage(this.index - 1);
    }

    buildModalMedia() {
      return this.slides
        .map((slide, slideIndex) => {
          const type = slide.dataset.mediaType || 'image';
          const mediaId = slide.dataset.mediaId || String(slideIndex);

          if (type === 'image') {
            const img = slide.querySelector('img');
            if (!img) return null;

            return {
              type: 'image',
              mediaId,
              slideIndex,
              src: img.getAttribute('src') || img.src || '',
              srcset: img.getAttribute('srcset') || '',
              sizes: img.getAttribute('sizes') || '',
              alt: img.getAttribute('alt') || '',
              width: Number(img.getAttribute('width')) || img.naturalWidth || 0,
              height: Number(img.getAttribute('height')) || img.naturalHeight || 0,
            };
          }

          if (type === 'video') {
            const video = slide.querySelector('video');
            if (!video) return null;
            this.prepareVideo(video);

            return {
              type: 'video',
              mediaId,
              slideIndex,
              sourceVideo: video,
            };
          }

          return null;
        })
        .filter(Boolean);
    }

    init() {
      this.goTo(0, {
        instant: true,
      });

      this.on(this.prev, 'click', () =>
        this.goTo(this.index - 1)
      );

      this.on(this.next, 'click', () =>
        this.goTo(this.index + 1)
      );

      this.dots.forEach((dot) => {
        this.on(dot, 'click', () => {
          const index = Number(
            dot.dataset.index || 0
          );

          this.goTo(index);
        });
      });

      this.slides.forEach((slide) => {
        this.on(slide, 'click', (event) => {
          if (this.isBlockingInteractiveMediaClick(event)) {
            return;
          }

          if (
            slide.dataset.mediaType &&
            slide.dataset.mediaType !== 'image' &&
            slide.dataset.mediaType !== 'video'
          ) {
            return;
          }

          if (
            this.isMobile() &&
            Date.now() - this.lastTouchMoveAt < 260
          ) {
            return;
          }

          this.openModal(this.index, slide);
        });

        this.on(slide, 'keydown', (event) => {
          if (
            (slide.dataset.mediaType !== 'image' && slide.dataset.mediaType !== 'video') ||
            (event.key !== 'Enter' && event.key !== ' ')
          ) {
            return;
          }

          event.preventDefault();
          this.openModal(this.index, slide);
        });
      });

      this.on(this.viewport, 'click', (event) => {
        if (
          event.target.closest(
            '[data-mg-gallery-prev], [data-mg-gallery-next]'
          )
        ) {
          return;
        }

        if (this.isBlockingInteractiveMediaClick(event)) {
          return;
        }

        const activeSlide =
          this.slides[this.index];

        if (
          activeSlide &&
          activeSlide.dataset.mediaType &&
          activeSlide.dataset.mediaType !== 'image' &&
          activeSlide.dataset.mediaType !== 'video'
        ) {
          return;
        }

        if (
          Date.now() - this.lastTouchMoveAt <
          260
        ) {
          return;
        }

        this.openModal(this.index, activeSlide);
      });

      this.bindMobileDragging();

      this.on(
        this.closeButton,
        'click',
        () => this.closeModal()
      );

      this.on(
        this.modalPrev,
        'click',
        () => this.showModalMedia(this.modalIndex - 1, -1)
      );

      this.on(
        this.modalNext,
        'click',
        () => this.showModalMedia(this.modalIndex + 1, 1)
      );

      this.on(
        this.modalStage,
        'click',
        (event) => {
          if (!event.target.closest('.mg-product-gallery-modal__image, .mg-product-gallery-modal__video')) {
            this.closeModal();
          }
        }
      );

      this.on(
        this.modalStage,
        'touchstart',
        (event) => {
          if (!event.touches || event.touches.length !== 1) {
            return;
          }

          this.modalTouchStartX = event.touches[0].clientX;
          this.modalTouchStartY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.on(
        this.modalStage,
        'touchmove',
        (event) => {
          if (!event.touches || event.touches.length !== 1 || !event.cancelable) {
            return;
          }

          const dx = event.touches[0].clientX - this.modalTouchStartX;
          const dy = event.touches[0].clientY - this.modalTouchStartY;

          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
            event.preventDefault();
            this.pauseModalFrame(
              this.modalStage.querySelector('.mg-product-gallery-modal__frame.is-active')
            );
          }
        },
        { passive: false }
      );

      this.on(
        this.modalStage,
        'touchend',
        (event) => {
          if (!event.changedTouches || !event.changedTouches.length) {
            return;
          }

          const dx = event.changedTouches[0].clientX - this.modalTouchStartX;
          const dy = event.changedTouches[0].clientY - this.modalTouchStartY;

          if (Math.abs(dx) > 46 && Math.abs(dx) > Math.abs(dy) * 1.15) {
            this.showModalMedia(this.modalIndex + (dx < 0 ? 1 : -1), dx < 0 ? 1 : -1);
          } else {
            this.playModalFrame(
              this.modalStage.querySelector('.mg-product-gallery-modal__frame.is-active')
            );
          }
        },
        { passive: true }
      );

      this.on(document, 'keydown', (event) => {
        const modalOpen = this.modal && !this.modal.hidden;

        if (event.key === 'Escape' && modalOpen) {
          this.closeModal();
          return;
        }

        if (event.key === 'ArrowLeft') {
          if (modalOpen) {
            event.preventDefault();
            this.showModalMedia(this.modalIndex - 1, -1);
          } else if (!this.isMobile()) {
            this.goTo(this.index - 1);
          }
        }

        if (event.key === 'ArrowRight') {
          if (modalOpen) {
            event.preventDefault();
            this.showModalMedia(this.modalIndex + 1, 1);
          } else if (!this.isMobile()) {
            this.goTo(this.index + 1);
          }
        }
      });

      this.on(window, 'popstate', () => {
        if (
          this.modal &&
          !this.modal.hidden &&
          this.historyPushed
        ) {
          this.closeModal({
            fromHistory: true,
          });
        }
      });

      this.on(this.section, 'change', () => {
        window.setTimeout(
          () => this.syncVariantMedia(),
          80
        );
      });

      this.on(window, 'resize', () => {
        this.goTo(this.index, {
          instant: true,
        });
      });
    }

    bindMobileDragging() {
      if (!this.viewport || !this.track) {
        return;
      }

      this.on(
        this.viewport,
        'touchstart',
        (event) => {
          if (
            !this.isMobile() ||
            !event.touches ||
            !event.touches.length
          ) {
            return;
          }

          const touch = event.touches[0];

          this.drag.active = true;
          this.drag.locked = null;
          this.drag.startX =
            touch.clientX;
          this.drag.startY =
            touch.clientY;
          this.drag.deltaX = 0;

          this.track.style.transition =
            'none';
        },
        {
          passive: true,
        }
      );

      this.on(
        this.viewport,
        'touchmove',
        (event) => {
          if (
            !this.isMobile() ||
            !this.drag.active ||
            !event.touches ||
            !event.touches.length
          ) {
            return;
          }

          const touch = event.touches[0];

          const dx =
            touch.clientX -
            this.drag.startX;

          const dy =
            touch.clientY -
            this.drag.startY;

          if (!this.drag.locked) {
            if (
              Math.abs(dx) < 8 &&
              Math.abs(dy) < 8
            ) {
              return;
            }

            this.drag.locked =
              Math.abs(dx) > Math.abs(dy)
                ? 'x'
                : 'y';
          }

          if (this.drag.locked === 'x') {
            if (event.cancelable) {
              event.preventDefault();
            }

            this.pauseAllSlideVideos();
            this.lastTouchMoveAt =
              Date.now();

            const resistance =
              this.isAtEdge(dx)
                ? 0.28
                : 1;

            this.drag.deltaX =
              dx * resistance;

            this.applyMobileTransform(
              this.index,
              this.drag.deltaX,
              false
            );
          }
        },
        {
          passive: false,
        }
      );

      this.on(
        this.viewport,
        'touchend',
        () => {
          if (
            !this.isMobile() ||
            !this.drag.active
          ) {
            return;
          }

          const dx =
            this.drag.deltaX;

          const threshold = Math.min(
            78,
            Math.max(
              42,
              this.viewport.clientWidth *
                0.16
            )
          );

          let nextIndex =
            this.index;

          if (
            this.drag.locked === 'x' &&
            Math.abs(dx) > threshold
          ) {
            nextIndex =
              dx < 0
                ? this.index + 1
                : this.index - 1;
          }

          const wraps =
            nextIndex < 0 ||
            nextIndex >=
              this.slides.length;

          this.drag.active = false;
          this.drag.locked = null;
          this.drag.deltaX = 0;

          if (wraps && this.slides.length > 1) {
            this.animateMobileWrap(nextIndex > this.index ? 1 : -1);
          } else {
            this.goTo(nextIndex);
          }
        },
        {
          passive: true,
        }
      );

      this.on(
        this.viewport,
        'touchcancel',
        () => {
          if (!this.isMobile()) {
            return;
          }

          this.drag.active = false;
          this.drag.locked = null;
          this.drag.deltaX = 0;

          this.goTo(this.index);
        },
        {
          passive: true,
        }
      );
    }

    animateMobileWrap(direction) {
      if (!this.track || this.slides.length < 2) return;

      window.clearTimeout(this.mobileWrapTimer);

      const count = this.slides.length;
      const forward = direction > 0;
      const source = forward ? this.slides[0] : this.slides[count - 1];
      const clone = source.cloneNode(true);

      clone.classList.add('mg-product-gallery__wrap-clone');
      clone.classList.remove('is-active');
      clone.setAttribute('aria-hidden', 'true');
      clone.tabIndex = -1;
      clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
      clone.querySelectorAll('button, a, input, select, textarea, video, model-viewer, iframe').forEach((node) => {
        node.tabIndex = -1;
        node.setAttribute('aria-hidden', 'true');
      });

      const sourceVideos = Array.from(source.querySelectorAll('video'));
      const cloneVideos = Array.from(clone.querySelectorAll('video'));

      cloneVideos.forEach((cloneVideo, videoIndex) => {
        const sourceVideo = sourceVideos[videoIndex];
        this.prepareVideo(cloneVideo);
        cloneVideo.preload = 'auto';

        const startCloneVideo = () => {
          if (sourceVideo && Number.isFinite(sourceVideo.currentTime)) {
            try { cloneVideo.currentTime = sourceVideo.currentTime; } catch (error) {}
          }
          this.safePlayVideo(cloneVideo);
        };

        if (cloneVideo.readyState >= 1) {
          startCloneVideo();
        } else {
          cloneVideo.addEventListener('loadedmetadata', startCloneVideo, { once: true });
          if (typeof cloneVideo.load === 'function') cloneVideo.load();
        }
      });

      let finished = false;
      const finish = () => {
        if (finished) return;
        finished = true;
        window.clearTimeout(this.mobileWrapTimer);
        this.track.removeEventListener('transitionend', onTransitionEnd);

        cloneVideos.forEach((cloneVideo, videoIndex) => {
          const sourceVideo = sourceVideos[videoIndex];
          cloneVideo.pause();
          if (sourceVideo && Number.isFinite(cloneVideo.currentTime)) {
            try { sourceVideo.currentTime = cloneVideo.currentTime; } catch (error) {}
          }
        });

        clone.remove();
        this.goTo(forward ? 0 : count - 1, { instant: true });
      };

      const onTransitionEnd = (event) => {
        if (event.target !== this.track || event.propertyName !== 'transform') return;
        finish();
      };

      if (forward) {
        this.track.appendChild(clone);
        this.track.style.transition = 'transform 340ms cubic-bezier(0.22, 0.61, 0.36, 1)';

        window.requestAnimationFrame(() => {
          this.track.style.transform = `translate3d(${-count * 100}%, 0, 0)`;
        });
      } else {
        this.track.insertBefore(clone, this.track.firstChild);
        this.track.style.transition = 'none';
        this.track.style.transform = 'translate3d(-100%, 0, 0)';

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.track.style.transition = 'transform 340ms cubic-bezier(0.22, 0.61, 0.36, 1)';
            this.track.style.transform = 'translate3d(0, 0, 0)';
          });
        });
      }

      this.track.addEventListener('transitionend', onTransitionEnd);
      this.mobileWrapTimer = window.setTimeout(finish, 430);
    }

    isAtEdge(dx) {
      return false;
    }

    normalize(index) {
      if (!this.slides.length) {
        return 0;
      }

      const length =
        this.slides.length;

      return (
        ((index % length) + length) %
        length
      );
    }

    goTo(index, options = {}) {
      if (!this.slides.length) {
        return;
      }

      this.index =
        this.normalize(index);

      this.slides.forEach(
        (slide, slideIndex) => {
          const active =
            slideIndex === this.index;

          slide.classList.toggle(
            'is-active',
            active
          );

          if (slide.dataset.mediaType === 'image' || slide.dataset.mediaType === 'video') {
            slide.tabIndex = active ? 0 : -1;
          }
        }
      );

      this.dots.forEach(
        (dot, dotIndex) => {
          const active =
            dotIndex === this.index;

          dot.classList.toggle(
            'is-active',
            active
          );

          dot.setAttribute(
            'aria-selected',
            active
              ? 'true'
              : 'false'
          );
        }
      );

      if (this.isMobile()) {
        this.applyMobileTransform(
          this.index,
          0,
          !options.instant
        );
      }

      this.syncSlideVideoPlayback();
      this.preloadAdjacentSlides();
    }

    applyMobileTransform(
      index,
      offsetPx = 0,
      animate = true
    ) {
      if (!this.track) return;

      this.track.style.transition =
        animate
          ? 'transform 340ms cubic-bezier(0.22, 0.61, 0.36, 1)'
          : 'none';

      this.track.style.transform =
        `translate3d(calc(${
          -index * 100
        }% + ${offsetPx}px), 0, 0)`;
    }

    syncVariantMedia() {
      const input =
        this.section.querySelector(
          'form[action*="/cart/add"] input[name="id"]'
        );

      if (
        !input ||
        !this.data.variantMedia
      ) {
        return;
      }

      const mediaId =
        this.data.variantMedia[
          input.value
        ];

      if (!mediaId) {
        return;
      }

      const index =
        this.slides.findIndex(
          (slide) =>
            String(
              slide.dataset.mediaId
            ) ===
            String(mediaId)
        );

      if (index >= 0) {
        this.goTo(index);
      }
    }

    normalizeModalIndex(index) {
      if (!this.modalMedia.length) return 0;

      return (
        (index % this.modalMedia.length) +
        this.modalMedia.length
      ) % this.modalMedia.length;
    }

    buildModal() {
      if (!this.modalStage || this.modalBuilt) {
        return;
      }

      this.modalStage.innerHTML = '';
      this.modalBuilt = true;
    }

    createModalFrame(item, index) {
      const frame = document.createElement('figure');
      frame.className = 'mg-product-gallery-modal__frame';
      frame.dataset.index = String(index);
      frame.dataset.mediaId = String(item.mediaId || index);
      frame.dataset.mediaType = item.type || 'image';

      if (item.type === 'video' && item.sourceVideo) {
        const video = item.sourceVideo.cloneNode(true);
        video.className = 'mg-product-gallery-modal__video';
        video.removeAttribute('id');
        this.prepareVideo(video);
        video.preload = 'none';
        video.autoplay = false;
        video.removeAttribute('autoplay');
        video.setAttribute('aria-label', item.sourceVideo.getAttribute('aria-label') || '');
        frame.appendChild(video);
        return frame;
      }

      const img = document.createElement('img');
      img.className = 'mg-product-gallery-modal__image';

      if (item.srcset) {
        img.srcset = item.srcset;
      }

      img.sizes = item.sizes || '(max-width: 749px) calc(100vw - 24px), (pointer: coarse) and (orientation: landscape) and (max-width: 989px) and (max-height: 520px) calc(100vw - 24px), calc(100vw - 160px)';
      img.src = item.src || '';
      img.alt = item.alt || '';
      img.width = item.width || '';
      img.height = item.height || '';
      img.decoding = 'async';
      img.draggable = false;

      if ('fetchPriority' in img) {
        img.fetchPriority = 'high';
      }

      frame.appendChild(img);
      return frame;
    }

    pauseModalFrame(frame) {
      if (!frame) return;

      const index = Number(frame.dataset.index);
      const item = this.modalMedia[index];

      frame.querySelectorAll('video').forEach((video) => {
        video.pause();

        if (Number.isFinite(video.currentTime)) {
          const mediaKey = String((item && item.mediaId) || frame.dataset.mediaId || index);
          this.modalVideoTimes.set(mediaKey, video.currentTime);

          if (item && item.sourceVideo) {
            try {
              item.sourceVideo.currentTime = video.currentTime;
            } catch (error) {
              /* Metadata may not be ready yet. Resume time remains in the map. */
            }
          }
        }
      });
    }

    playModalFrame(frame) {
      if (!frame) return;

      const index = Number(frame.dataset.index);
      const item = this.modalMedia[index];
      const video = frame.querySelector('video');
      if (!video || !item) return;

      this.prepareVideo(video);
      const mediaKey = String(item.mediaId || index);
      const remembered = this.modalVideoTimes.get(mediaKey);
      const sourceTime = item.sourceVideo && Number.isFinite(item.sourceVideo.currentTime)
        ? item.sourceVideo.currentTime
        : 0;
      const resumeTime = Number.isFinite(remembered) ? remembered : sourceTime;

      // A cloned <video> starts visually at frame 0 even when we intend to
      // resume later. Keep it hidden until the requested frame is ready so
      // the fullscreen lightbox never flashes the first frame during a swipe.
      video.style.visibility = 'hidden';

      const revealAndPlay = () => {
        if (!frame.isConnected || !frame.classList.contains('is-active')) return;
        video.style.visibility = '';
        this.safePlayVideo(video);
      };

      const seekAndPlay = () => {
        if (!frame.isConnected || !frame.classList.contains('is-active')) return;

        if (!(Number.isFinite(resumeTime) && resumeTime > 0)) {
          if (video.readyState >= 2) {
            revealAndPlay();
          } else {
            video.addEventListener('loadeddata', revealAndPlay, { once: true });
            this.safePlayVideo(video);
          }
          return;
        }

        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const targetTime = duration > 0
          ? Math.min(resumeTime, Math.max(0, duration - 0.05))
          : resumeTime;

        if (Math.abs((video.currentTime || 0) - targetTime) < 0.04 && video.readyState >= 2) {
          revealAndPlay();
          return;
        }

        let revealed = false;
        const revealOnce = () => {
          if (revealed) return;
          revealed = true;
          revealAndPlay();
        };

        video.addEventListener('seeked', revealOnce, { once: true });

        try {
          video.currentTime = targetTime;
        } catch (error) {
          revealOnce();
          return;
        }

        // A defensive fallback for browsers that do not dispatch `seeked`
        // after restoring a very small/near-current time.
        window.setTimeout(revealOnce, 450);
      };

      if (video.readyState >= 1) {
        seekAndPlay();
      } else {
        video.addEventListener('loadedmetadata', seekAndPlay, { once: true });
        // Trigger loading only now, when this video is actually the active
        // fullscreen media. This preserves preload=none on initial page load.
        try { video.load(); } catch (error) {}
      }
    }

    preloadModalNeighbors(index) {
      if (this.modalMedia.length < 2) return;

      const indexes = [
        this.normalizeModalIndex(index + 1),
        this.normalizeModalIndex(index - 1),
      ];

      indexes.forEach((neighborIndex) => {
        const item = this.modalMedia[neighborIndex];
        if (!item || item.type !== 'image' || !item.src || this.modalPreloaded.has(neighborIndex)) return;

        const preload = new Image();
        if (item.srcset) preload.srcset = item.srcset;
        preload.sizes = item.sizes || '(max-width: 749px) calc(100vw - 24px), (pointer: coarse) and (orientation: landscape) and (max-width: 989px) and (max-height: 520px) calc(100vw - 24px), calc(100vw - 160px)';
        preload.decoding = 'async';
        if ('fetchPriority' in preload) preload.fetchPriority = 'low';
        preload.src = item.src;
        this.modalPreloaded.set(neighborIndex, preload);
      });
    }

    updateModalCounter() {
      if (!this.modalCounter) return;

      const total = this.modalMedia.length;
      this.modalCounter.textContent = total ? `${this.modalIndex + 1} / ${total}` : '';
    }

    showModalMedia(index, direction = 1, options = {}) {
      if (!this.modalStage || !this.modalMedia.length || this.modalAnimating) return;

      const normalized = this.normalizeModalIndex(index);
      const item = this.modalMedia[normalized];
      const current = this.modalStage.querySelector('.mg-product-gallery-modal__frame.is-active');

      if (current && Number(current.dataset.index) === normalized && !options.instant) {
        return;
      }

      window.clearTimeout(this.modalTransitionTimer);
      const renderToken = ++this.modalRenderToken;
      this.modalAnimating = !options.instant;

      const next = this.createModalFrame(item, normalized);
      next.classList.add(direction < 0 ? 'is-from-left' : 'is-from-right');
      this.modalStage.appendChild(next);

      const activate = () => {
        if (renderToken !== this.modalRenderToken || !next.isConnected) {
          this.pauseModalFrame(next);
          next.remove();
          this.modalAnimating = false;
          return;
        }

        if (current) {
          this.pauseModalFrame(current);
          current.classList.remove('is-active');
          current.classList.add(direction < 0 ? 'is-leaving-right' : 'is-leaving-left');
        }

        if (options.instant) {
          next.classList.add('is-no-transition');
        }

        window.requestAnimationFrame(() => {
          next.classList.add('is-active');
          next.classList.remove('is-from-left', 'is-from-right');
          this.playModalFrame(next);

          if (options.instant) {
            window.requestAnimationFrame(() => next.classList.remove('is-no-transition'));
          }
        });

        this.modalIndex = normalized;
        this.updateModalCounter();
        this.preloadModalNeighbors(normalized);

        this.modalTransitionTimer = window.setTimeout(() => {
          Array.from(this.modalStage.querySelectorAll('.mg-product-gallery-modal__frame')).forEach((frame) => {
            if (frame !== next) {
              this.pauseModalFrame(frame);
              frame.remove();
            }
          });
          this.modalAnimating = false;
        }, options.instant ? 30 : 330);
      };

      const img = next.querySelector('img');
      if (img && img.decode) {
        img.decode().then(activate).catch(activate);
      } else if (img && !img.complete) {
        img.addEventListener('load', activate, { once: true });
        img.addEventListener('error', activate, { once: true });
      } else {
        activate();
      }
    }

    openModal(index, trigger = null) {
      if (!this.modal || !this.modalStage || !this.modalMedia.length) {
        return;
      }

      if (!this.modal.hidden) return;

      this.modalTrigger =
        trigger ||
        (document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null);

      this.buildModal();
      this.pushHistoryState();
      this.lockBody();
      this.pauseAllSlideVideos();

      this.modal.hidden = false;
      this.modal.setAttribute('aria-hidden', 'false');
      this.modalPreloaded.clear();
      this.modalStage.innerHTML = '';

      const hasMultiple = this.modalMedia.length > 1;
      if (this.modalPrev) this.modalPrev.hidden = !hasMultiple;
      if (this.modalNext) this.modalNext.hidden = !hasMultiple;

      const mediaIndex = this.modalMedia.findIndex((item) =>
        String(item.slideIndex) === String(index)
      );

      this.showModalMedia(mediaIndex >= 0 ? mediaIndex : 0, 1, { instant: true });

      if (typeof trapFocus === 'function' && this.closeButton) {
        trapFocus(this.modal, this.closeButton);
      } else if (this.closeButton) {
        this.closeButton.focus({ preventScroll: true });
      }
    }

    pushHistoryState() {
      if (
        !this.isMobile() ||
        this.historyPushed ||
        !window.history ||
        !window.history.pushState
      ) {
        return;
      }

      try {
        window.history.pushState(
          {
            mgProductGalleryModal: true,
          },
          '',
          window.location.href
        );

        this.historyPushed = true;
      } catch (error) {
        this.historyPushed = false;
      }
    }

    closeModal(options = {}) {
      if (
        !this.modal ||
        this.modal.hidden
      ) {
        return;
      }

      if (
        !options.fromHistory &&
        this.historyPushed &&
        window.history &&
        window.history.back
      ) {
        this.closingFromHistory = true;

        window.history.back();

        window.clearTimeout(
          this.historyFallbackTimer
        );

        this.historyFallbackTimer =
          window.setTimeout(() => {
            if (
              this.closingFromHistory &&
              this.modal &&
              !this.modal.hidden
            ) {
              this.forceCloseModal();
            }
          }, 350);

        return;
      }

      this.forceCloseModal();
    }

    forceCloseModal(options = {}) {
      if (!this.modal || this.modal.hidden) {
        window.clearTimeout(
          this.historyFallbackTimer
        );
        this.historyPushed = false;
        this.closingFromHistory = false;
        this.modalTrigger = null;
        return;
      }

      window.clearTimeout(this.modalTransitionTimer);
      this.modalTransitionTimer = null;
      this.modalRenderToken += 1;
      this.modalAnimating = false;
      this.modalPreloaded.clear();
      Array.from(this.modalStage ? this.modalStage.querySelectorAll('.mg-product-gallery-modal__frame') : []).forEach((frame) => {
        this.pauseModalFrame(frame);
      });

      this.modal.hidden = true;

      this.modal.setAttribute(
        'aria-hidden',
        'true'
      );

      this.historyPushed = false;
      this.closingFromHistory = false;

      window.clearTimeout(
        this.historyFallbackTimer
      );

      this.unlockBody();
      this.syncSlideVideoPlayback();

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }

      if (
        options.restoreFocus !== false &&
        this.modalTrigger &&
        this.modalTrigger.isConnected &&
        typeof this.modalTrigger.focus === 'function'
      ) {
        this.modalTrigger.focus({
          preventScroll: true,
        });
      }

      this.modalTrigger = null;
    }

    lockBody() {
      this.scrollY =
        window.scrollY ||
        document.documentElement
          .scrollTop ||
        0;

      document.documentElement.classList.add(
        'mg-gallery-modal-open'
      );

      document.body.classList.add(
        'mg-gallery-modal-open'
      );

      document.body.style.position =
        'fixed';

      document.body.style.top =
        `-${this.scrollY}px`;

      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    }

    unlockBody() {
      document.documentElement.classList.remove(
        'mg-gallery-modal-open'
      );

      document.body.classList.remove(
        'mg-gallery-modal-open'
      );

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';

      window.scrollTo(
        0,
        this.scrollY || 0
      );
    }

    destroy() {
      this.bound.forEach(
        ([
          target,
          event,
          handler,
          options,
        ]) => {
          target.removeEventListener(
            event,
            handler,
            options
          );
        }
      );

      this.bound = [];

      window.clearTimeout(this.mobileWrapTimer);
      window.clearTimeout(this.modalTransitionTimer);
      this.pauseAllSlideVideos();

      this.forceCloseModal({ restoreFocus: false });

      registry.delete(this.root);
    }
  }

  function init(container = document) {
    container
      .querySelectorAll(
        '[data-mg-product-gallery]'
      )
      .forEach((root) => {
        if (registry.has(root)) {
          return;
        }

        registry.set(
          root,
          new MiniGearProductGallery(root)
        );
      });
  }

  function destroy(
    container = document
  ) {
    container
      .querySelectorAll(
        '[data-mg-product-gallery]'
      )
      .forEach((root) => {
        const instance =
          registry.get(root);

        if (instance) {
          instance.destroy();
        }
      });
  }

  document.addEventListener(
    'DOMContentLoaded',
    () => init()
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => init(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => destroy(event.target)
  );
})();